import React, { useState, useEffect } from 'react';
import axios from 'axios';
import WaveLoader from './WaveLoader';
import '../styles/DrilldownPanel.css';

function DrilldownPanel({ drilldown, filters, onClose, onNavigateToConversation }) {
  const [conversations, setConversations] = useState([]);
  const [entityMentions, setEntityMentions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!drilldown) return;
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params.append(k, v);
    });
    const ctx = drilldown.context || {};

    if (drilldown.type === 'conversations') {
      setLoading(true);
      setError(null);
      if (ctx.agent_id != null) params.append('agent_id', ctx.agent_id);
      if (ctx.has_appointment != null) params.append('has_appointment', ctx.has_appointment ? 1 : 0);
      if (ctx.channel_id != null) params.append('channel_id', ctx.channel_id);
      if (ctx.conversation_id != null) params.append('conversation_id', ctx.conversation_id);

      axios.get(`/api/conversations?${params}`)
        .then(res => setConversations(res.data.data || []))
        .catch(err => {
          setError(err.response?.data?.error || err.message || 'Failed to load conversations');
          setConversations([]);
        })
        .finally(() => setLoading(false));
    } else if (drilldown.type === 'entity-type' || drilldown.type === 'entity-conversations') {
      setLoading(true);
      setError(null);
      if (ctx.entity_type) params.append('entity_type', ctx.entity_type);
      if (ctx.entity_name) params.append('entity_name', ctx.entity_name);
      if (ctx.outcome_status) params.append('outcome_status', ctx.outcome_status);
      if (ctx.region_name) params.append('region_name', ctx.region_name);

      axios.get(`/api/field-intel/entity-mentions?${params}`)
        .then(res => setEntityMentions(res.data.data || []))
        .catch(err => {
          setError(err.response?.data?.error || err.message || 'Failed to load entity mentions');
          setEntityMentions([]);
        })
        .finally(() => setLoading(false));
    }
  }, [drilldown, filters]);

  if (!drilldown) return null;

  const isConversations = drilldown.type === 'conversations';
  const isEntityDrilldown = drilldown.type === 'entity-type' || drilldown.type === 'entity-conversations';
  const isWide = isConversations || isEntityDrilldown;
  const title = drilldown.context?.title || (isConversations ? 'Conversations' : drilldown.type.charAt(0).toUpperCase() + drilldown.type.slice(1) + ' Details');

  const renderEntityTable = () => {
    if (loading) return <WaveLoader size="small" />;
    if (error) return <div style={{ padding: '16px', background: '#fff3cd', borderRadius: 8, marginBottom: 16, color: '#856404' }}>{error}</div>;
    if (entityMentions.length === 0) return <div style={{ textAlign: 'center', padding: '24px', color: '#999' }}>No results found for this selection.</div>;

    const isTypeView = drilldown.type === 'entity-type';
    return (
      <div className="conversations-table-wrap">
        <table className="data-table conversations-table">
          <thead>
            <tr>
              <th>Conversation ID</th>
              <th>Date</th>
              {isTypeView ? <th>Mention Text</th> : <th>Agent</th>}
            </tr>
          </thead>
          <tbody>
            {entityMentions.map((row, i) => (
              <tr key={`${row.conversation_id}-${i}`}>
                <td>
                  {onNavigateToConversation ? (
                    <button
                      type="button"
                      onClick={() => { onNavigateToConversation(row.conversation_id); }}
                      style={{
                        background: 'none', border: 'none', padding: 0,
                        color: '#1e3a5f', textDecoration: 'underline',
                        cursor: 'pointer', fontFamily: 'monospace', fontSize: '13px',
                      }}
                      title="Open in Conversations tab"
                    >
                      {row.conversation_id}
                    </button>
                  ) : row.conversation_id}
                </td>
                <td>{row.call_date ? new Date(row.call_date).toLocaleDateString() : '-'}</td>
                {isTypeView ? (
                  <td style={{ maxWidth: 400, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {row.mention_text || '-'}
                  </td>
                ) : (
                  <td>{row.agent_name || '-'}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: '12px', color: '#999', marginTop: 12 }}>
          Showing up to 500 results.
        </p>
      </div>
    );
  };

  return (
    <div className="drilldown-overlay" onClick={onClose}>
      <div className={`drilldown-panel ${isWide ? 'drilldown-panel-wide' : ''}`} onClick={e => e.stopPropagation()}>
        {/* X on the panel edge (not over the sidebar) */}
        <button
          type="button"
          className="drilldown-overlay-close"
          onClick={onClose}
          title="Close"
          aria-label="Close panel"
        >
          ×
        </button>
        <div className="drilldown-header">
          <h2>{title}</h2>
          <button type="button" className="close-btn" onClick={onClose} title="Close" aria-label="Close">
            ×
          </button>
        </div>

        <div className="drilldown-content">
          {isConversations ? (
            <>
              {loading && <div style={{ textAlign: 'center', padding: '24px', color: '#666' }}>Loading conversations...</div>}
              {error && <div style={{ padding: '16px', background: '#fff3cd', borderRadius: 8, marginBottom: 16, color: '#856404' }}>{error}</div>}
              {!loading && !error && conversations.length === 0 && (
                <div style={{ textAlign: 'center', padding: '24px', color: '#999' }}>No conversations found for this selection.</div>
              )}
              {!loading && !error && conversations.length > 0 && (
                <div className="conversations-table-wrap">
                  <table className="data-table conversations-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Agent</th>
                        <th>Region</th>
                        <th>Outcome</th>
                        <th>Appt</th>
                        <th>Sentiment</th>
                        <th>Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conversations.map(row => (
                        <tr key={row.conversation_id}>
                          <td>{row.call_date ? new Date(row.call_date).toLocaleDateString() : '-'}</td>
                          <td>{row.agent_name || '-'}</td>
                          <td>{row.region_name || '-'}</td>
                          <td>{row.outcome_status || '-'}</td>
                          <td>{row.has_appointment === 1 ? '✓' : '-'}</td>
                          <td>{row.overall_sentiment != null ? Number(row.overall_sentiment).toFixed(2) : '-'}</td>
                          <td>{row.conversion_confidence != null ? (Number(row.conversion_confidence) * 100).toFixed(0) + '%' : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p style={{ fontSize: '12px', color: '#999', marginTop: 12 }}>
                    Showing up to 500 conversations. Filters: time range, region, team, date type.
                  </p>
                </div>
              )}
            </>
          ) : isEntityDrilldown ? (
            renderEntityTable()
          ) : (
            <>
              <div className="detail-section">
                <h4>Details</h4>
                <dl>
                  {Object.entries(drilldown.context || {}).map(([key, value]) => (
                    <div key={key} className="detail-row">
                      <dt>{key}</dt>
                      <dd>{typeof value === 'number' ? value.toFixed(2) : value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div className="detail-section">
                <h4>Filters Applied</h4>
                <dl>
                  {Object.entries(filters).filter(([_, v]) => v !== null).map(([key, value]) => (
                    <div key={key} className="detail-row">
                      <dt>{key}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DrilldownPanel;
